import { useState } from 'react';
import type { FormEvent } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Alert } from '@/components/ui/Alert';
import { authApi } from '@/services/api/auth';

interface LoginFormData {
    email: string;
    password: string;
}

interface LoginFormErrors {
    email?: string;
    password?: string;
}

const validate = (data: LoginFormData): LoginFormErrors => {
    const errors: LoginFormErrors = {};
    if (!data.email.trim()) {
        errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
        errors.email = 'Enter a valid email address';
    }
    if (!data.password) {
        errors.password = 'Password is required';
    }
    return errors;
};

type LoginView = 'signin' | 'signup' | 'reset' | 'reset-success';

export const LoginPage = () => {
    const [view, setView] = useState<LoginView>('signin');
    const [activeTab, setActiveTab] = useState<'signin' | 'signup'>('signin');

    // Sign-in form state
    const [formData, setFormData] = useState<LoginFormData>({ email: '', password: '' });
    const [errors, setErrors] = useState<LoginFormErrors>({});
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // Reset flow state
    const [resetEmail, setResetEmail] = useState('');
    const [resetError, setResetError] = useState<string | null>(null);
    const [resetLoading, setResetLoading] = useState(false);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setSubmitError(null);

        const validationErrors = validate(formData);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setErrors({});

        setIsLoading(true);
        const { error } = await authApi.signIn(formData);
        setIsLoading(false);

        if (error) {
            setSubmitError(error.message);
        }
        // On success, supabase.auth.onAuthStateChange fires in useAuth,
        // which triggers App to unmount LoginPage and render the main routes.
    };

    const handleChange = (field: keyof LoginFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({ ...prev, [field]: e.target.value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const handleResetSubmit = async () => {
        if (!resetEmail.trim()) return;
        setResetLoading(true);
        setResetError(null);
        const { error } = await authApi.resetPassword(resetEmail.trim());
        setResetLoading(false);
        if (error) {
            setResetError(error.message);
        } else {
            setView('reset-success');
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
            <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8">
                <div className="mb-6 text-center">
                    <h1 className="text-2xl font-bold text-gray-900">PlanArc</h1>
                </div>

                {/* Tab toggle — shown for signin/signup views only */}
                {(view === 'signin' || view === 'signup') && (
                    <div className="flex border-b border-gray-200 mb-6">
                        <button
                            role="tab"
                            aria-selected={activeTab === 'signin'}
                            onClick={() => { setActiveTab('signin'); setView('signin'); }}
                            className={`flex-1 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                                activeTab === 'signin'
                                    ? 'border-primary-600 text-primary-700'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            Sign In
                        </button>
                        <button
                            role="tab"
                            aria-selected={activeTab === 'signup'}
                            onClick={() => setActiveTab('signup')}
                            className={`flex-1 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                                activeTab === 'signup'
                                    ? 'border-primary-600 text-primary-700'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            Create Account
                        </button>
                    </div>
                )}

                {/* Sign In tab content */}
                {view === 'signin' && activeTab === 'signin' && (
                    <>
                        {submitError && (
                            <div className="mb-4">
                                <Alert variant="error">{submitError}</Alert>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} noValidate className="space-y-4">
                            <Input
                                label="Email"
                                type="email"
                                autoComplete="email"
                                required
                                value={formData.email}
                                onChange={handleChange('email')}
                                error={errors.email}
                            />
                            <div>
                                <Input
                                    label="Password"
                                    type="password"
                                    autoComplete="current-password"
                                    required
                                    value={formData.password}
                                    onChange={handleChange('password')}
                                    error={errors.password}
                                />
                                <div className="text-right mt-1">
                                    <button
                                        type="button"
                                        onClick={() => setView('reset')}
                                        className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
                                    >
                                        Forgot password?
                                    </button>
                                </div>
                            </div>
                            <Button
                                type="submit"
                                className="w-full"
                                loading={isLoading}
                                disabled={isLoading}
                            >
                                Sign in
                            </Button>
                        </form>
                    </>
                )}

                {/* Create Account tab content */}
                {activeTab === 'signup' && (view === 'signin' || view === 'signup') && (
                    <div className="text-center space-y-4 py-4">
                        <p className="text-sm text-gray-700">
                            PlanArc is currently in private beta. Public sign-ups are coming soon.
                        </p>
                        <button
                            disabled
                            aria-disabled="true"
                            title="Sign-ups coming soon"
                            className="w-full px-4 py-2 bg-primary-300 text-white font-medium rounded-lg cursor-not-allowed opacity-60"
                        >
                            Create Account
                        </button>
                    </div>
                )}

                {/* Password reset view */}
                {view === 'reset' && (
                    <div className="space-y-4">
                        <h2 className="text-lg font-semibold text-gray-900">Reset your password</h2>
                        <p className="text-sm text-gray-600">
                            Enter your email and we&apos;ll send you a reset link.
                        </p>
                        {resetError && <Alert variant="error">{resetError}</Alert>}
                        <Input
                            label="Email"
                            type="email"
                            value={resetEmail}
                            onChange={(e) => setResetEmail(e.target.value)}
                            autoComplete="email"
                        />
                        <Button
                            type="button"
                            className="w-full"
                            loading={resetLoading}
                            onClick={handleResetSubmit}
                        >
                            Send Reset Link
                        </Button>
                        <button
                            type="button"
                            onClick={() => setView('signin')}
                            className="text-sm text-primary-600 hover:underline"
                        >
                            ← Back to Sign In
                        </button>
                    </div>
                )}

                {/* Reset success view */}
                {view === 'reset-success' && (
                    <div className="text-center space-y-4 py-4">
                        <p className="text-lg font-semibold text-gray-900">Check your inbox</p>
                        <p className="text-sm text-gray-600">
                            A reset link was sent to <strong>{resetEmail}</strong>.
                        </p>
                        <Button
                            type="button"
                            variant="secondary"
                            className="w-full"
                            onClick={() => { setView('signin'); setActiveTab('signin'); setResetEmail(''); }}
                        >
                            Back to Sign In
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};
